import { Injectable } from "@angular/core";
import {
	AssetOrderingOptions,
	GetStakingFilteredAssetsGQLService,
	GetStakingFilteredAssetsQuery,
	GetStakingInfoGQLService,
	GetStakingInfoQuery,
	GetStakingRewardsGQLService,
	GetStakingRewardsQuery,
	Order,
	StakeGQLService,
	StakeMutation,
	StakingActionInput,
	StakingActionType,
	StakingHistoryOptions,
	StakingStatusType,
	StakingType,
	UnstakeGQLService,
	UnstakeMutation
} from "@gql/staking";
import { EMPTY, Observable } from "rxjs";
import { ExceptionsService } from "@core-services/exceptions.service";
import { catchError, map } from "rxjs/operators";
import { InfoService } from "@core-services/info.service";
import { ApicallOption, ApicallService } from "@core-services/apicall.service";
import { TradableWalletService } from "@main-services/wallet/tradable_wallet.service";
import { TradableWallet } from "@models/interfaces/tradable_wallet";
import { TradableTransaction } from "@models/transaction";
import { Tradable } from "@models/interfaces/tradable";
import {
	GetPendingTransactionsGQLService,
	GetPendingTransactionsQuery
} from "../../../../api/graphql/staking/generated/staking.pending-transactions.service.graphql-gen";

export interface AssetFilterObject {
	option: AssetOrderingOptions;
	order: Order;
}

export enum StakeableFilterOptions {
	YIELD_ASC = "YIELD_ASC",
	YIELD_DESC = "YIELD_DESC"
}

@Injectable({
	providedIn: "root"
})
export class StakingRemote {
	public constructor(
		private readonly stakeService: StakeGQLService,
		private readonly unstakeService: UnstakeGQLService,
		private readonly stakingInfoService: GetStakingInfoGQLService,
		private readonly stakingFilteredAssetsService: GetStakingFilteredAssetsGQLService,
		private readonly stakingRewardsService: GetStakingRewardsGQLService,
		private readonly exceptionsService: ExceptionsService,
		private readonly pendingTransactionsService: GetPendingTransactionsGQLService,
		private readonly infoService: InfoService,
		private readonly apicallService: ApicallService
	) {}

	public fetchStakingInfo(): Observable<GetStakingInfoQuery> {
		return this.stakingInfoService.watch({}, { fetchPolicy: "network-only" }).valueChanges.pipe(
			map(({ data }) => data),
			catchError(this.exceptionsService.extractGqlErrorAndRethrow),
			catchError((error) => {
				this.infoService.handleApiError(error);
				return EMPTY;
			})
		);
	}

	public filterSwitch(filter: string): AssetFilterObject {
		let filterObject: AssetFilterObject = {
			option: AssetOrderingOptions.YearlyInterest,
			order: Order.Asc
		};
		switch (filter) {
			case StakeableFilterOptions.YIELD_ASC:
				filterObject.option = AssetOrderingOptions.YearlyInterest;
				filterObject.order = Order.Asc;
				return filterObject;
			case StakeableFilterOptions.YIELD_DESC:
				filterObject.option = AssetOrderingOptions.YearlyInterest;
				filterObject.order = Order.Desc;
				return filterObject;
			default:
				return filterObject;
		}
	}

	public fetchFilteredAssets(searchInput: string, filter): Observable<GetStakingFilteredAssetsQuery> {
		const filterOption = this.filterSwitch(filter);
		return this.stakingFilteredAssetsService
			.watch(
				{
					filter: { or: [{ name: { contains: searchInput } }, { symbol: { contains: searchInput } }] },
					order: { orderBy: filterOption?.option, order: filterOption?.order }
				},
				{ fetchPolicy: "network-only" }
			)
			.valueChanges.pipe(
				map(({ data }) => data),
				catchError(this.exceptionsService.extractGqlErrorAndRethrow),
				catchError((error) => {
					this.infoService.handleApiError(error);
					return EMPTY;
				})
			);
	}

	public fetchStakingRewards(): Observable<GetStakingRewardsQuery> {
		return this.stakingRewardsService.watch({}, { fetchPolicy: "network-only" }).valueChanges.pipe(
			map(({ data }) => data),
			catchError(this.exceptionsService.extractGqlErrorAndRethrow),
			catchError((error) => {
				this.infoService.handleApiError(error);
				return EMPTY;
			})
		);
	}

	public stake(stakeAction: StakingActionInput): Observable<StakeMutation> {
		return this.stakeService.mutate({ input: stakeAction }).pipe(
			map(({ data }) => data),
			catchError(this.exceptionsService.extractGqlErrorAndRethrow),
			catchError((error) => {
				this.infoService.handleApiError(error);
				return EMPTY;
			})
		);
	}

	public unstake(unstakeAction: StakingActionInput): Observable<UnstakeMutation> {
		return this.unstakeService.mutate({ input: unstakeAction }).pipe(
			map(({ data }) => data),
			catchError(this.exceptionsService.extractGqlErrorAndRethrow),
			catchError((error) => {
				this.infoService.handleApiError(error);
				return EMPTY;
			})
		);
	}

	public refetchDefaultWallet(
		walletId: string,
		walletService: TradableWalletService<TradableWallet, TradableTransaction, Tradable>
	): Observable<TradableWallet> {
		return this.apicallService.get("wallet/" + walletId, new ApicallOption(walletService.WalletType)).pipe(
			map((wallet: TradableWallet[]) => wallet[0]),
			catchError(this.exceptionsService.extractGqlErrorAndRethrow),
			catchError((error) => {
				this.infoService.handleApiError(error);
				return EMPTY;
			})
		);
	}

	public fetchPendingTransactions$(id: string): Observable<GetPendingTransactionsQuery> {
		return this.pendingTransactionsService
			.watch(
				{
					filter: {
						asset: { equals: id },
						type: { equals: StakingActionType.Unstake },
						// @ts-ignore
						status: StakingStatusType.Pending,
						stakingType: { equals: StakingType.Locked }
					},
					order: { orderBy: StakingHistoryOptions.ReleaseTimestamp, order: Order.Asc }
				},
				{ fetchPolicy: "network-only" }
			)
			.valueChanges.pipe(
				map(({ data }) => data),
				catchError(this.exceptionsService.extractGqlErrorAndRethrow),
				catchError((error) => {
					this.infoService.handleApiError(error);
					return EMPTY;
				})
			);
	}
}
